import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Bell, Brain, CalendarDays, Check, ChevronDown, CircleDashed, FileText, Gauge,
  Instagram, Linkedin, Megaphone, MoreHorizontal, Plus, ShieldCheck, Sparkles, X,
  LogOut, Terminal, KeyRound, Zap, LogIn, Edit3, Trash2, Radio
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HTTP = axios.create({ baseURL: API, withCredentials: true });

const channelIcon = { Instagram, Linkedin, Facebook: Megaphone, "Google Ads": Gauge };

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---------- Modal ----------
function Modal({ open, onClose, title, subtitle, wide, children, footer, testid }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid={testid || "modal"}>
      <div className={`modal-card ${wide ? "wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" onClick={onClose} data-testid="modal-close-button"><X size={16} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ---------- Login ----------
function Login({ onSuccess }) {
  const [email, setEmail] = useState("admin@blooprint.in");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await HTTP.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="login-wrap" data-testid="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="mark">B</div>
          <div>
            <h2>Blooprint Social</h2>
            <small>Internal Automation Console</small>
          </div>
        </div>
        <h1>Welcome back.</h1>
        <p>Sign in to manage brand brains, drafts, and the MCP surface.</p>
        <form onSubmit={submit}>
          <label className="field">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   autoComplete="email" required data-testid="login-email-input" />
          </label>
          <label className="field">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   autoComplete="current-password" required data-testid="login-password-input" />
          </label>
          <button type="submit" className="login-submit" disabled={busy} data-testid="login-submit-button">
            <LogIn size={16} /> {busy ? "Signing in…" : "Sign in"}
          </button>
          {error && <div className="login-error" data-testid="login-error">{error}</div>}
        </form>
        <div className="login-hint">
          <b>Roles</b> · admin (full) · approver (draft actions, learnings)<br />
          MCP endpoint: <b>/api/mcp</b> — Bearer token issued at login.
        </div>
      </div>
    </div>
  );
}

// ---------- Client form (create + edit) ----------
function ClientForm({ initial, onSave, onDelete, busy, isCreate }) {
  const [f, setF] = useState({
    id: initial?.id || "",
    name: initial?.name || "",
    descriptor: initial?.descriptor || "",
    initials: initial?.initials || "",
    color: initial?.color || "#3B82F6",
    accent: initial?.accent || "#22D3EE",
    timezone: initial?.timezone || "UTC",
    cadence: initial?.cadence || "3 posts / week",
    approver: initial?.approver || "",
    platforms: (initial?.platforms || ["LinkedIn", "Instagram"]).join(", "),
  });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = () => {
    const payload = { ...f, platforms: f.platforms.split(",").map((s) => s.trim()).filter(Boolean) };
    if (isCreate) {
      payload.ad_accounts = { meta: "", google: "" };
    } else {
      delete payload.id;
    }
    onSave(payload);
  };

  return (
    <div>
      <div className="form-grid">
        {isCreate && (
          <label className="field">
            <label>Workspace id (kebab-case, unique)</label>
            <input value={f.id} onChange={upd("id")} placeholder="neopticon" data-testid="client-id-input" />
          </label>
        )}
        <label className="field">
          <label>Brand name</label>
          <input value={f.name} onChange={upd("name")} data-testid="client-name-input" />
        </label>
        <label className="field">
          <label>Descriptor</label>
          <input value={f.descriptor} onChange={upd("descriptor")} placeholder="Design studio · Wellness clinic" data-testid="client-descriptor-input" />
        </label>
        <label className="field">
          <label>Initials (badge)</label>
          <input value={f.initials} onChange={upd("initials")} placeholder="NP" data-testid="client-initials-input" />
        </label>
        <label className="field">
          <label>Timezone</label>
          <input value={f.timezone} onChange={upd("timezone")} placeholder="Asia/Kolkata" data-testid="client-timezone-input" />
        </label>
        <label className="field">
          <label>Posting cadence</label>
          <input value={f.cadence} onChange={upd("cadence")} placeholder="3 posts / week" data-testid="client-cadence-input" />
        </label>
        <label className="field">
          <label>Approver name</label>
          <input value={f.approver} onChange={upd("approver")} data-testid="client-approver-input" />
        </label>
        <label className="field">
          <label>Platforms (comma-separated)</label>
          <input value={f.platforms} onChange={upd("platforms")} placeholder="LinkedIn, Instagram, Facebook" data-testid="client-platforms-input" />
        </label>
        <label className="field">
          <label>Primary color</label>
          <input type="color" value={f.color} onChange={upd("color")} data-testid="client-color-input" />
        </label>
        <label className="field">
          <label>Accent color</label>
          <input type="color" value={f.accent} onChange={upd("accent")} data-testid="client-accent-input" />
        </label>
      </div>
      <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {!isCreate && onDelete && (
          <button className="cta-outline" style={{ color: "var(--danger)", borderColor: "rgba(248,113,113,0.35)" }}
                  onClick={onDelete} data-testid="delete-client-button">
            <Trash2 size={14} /> Delete workspace
          </button>
        )}
        <button className="cta-primary" onClick={submit} disabled={busy} style={{ marginLeft: "auto" }}
                data-testid="save-client-button">
          {busy ? "Saving…" : isCreate ? "Create workspace" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ---------- Editable Brain ----------
function BrainView({ client, isEditor, setToast, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rule, setRule] = useState("");

  useEffect(() => {
    const b = client.brain || {};
    setF({
      positioning: b.positioning || "",
      audience: b.audience || "",
      tone: b.tone || "",
      no_go: b.no_go || "",
      pillars: (b.pillars || []).join(", "),
      platform_rules: Object.entries(b.platform_rules || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
    });
    setEdit(false);
  }, [client.id]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        positioning: f.positioning,
        audience: f.audience,
        tone: f.tone,
        no_go: f.no_go,
        pillars: f.pillars.split(",").map((s) => s.trim()).filter(Boolean),
        platform_rules: Object.fromEntries(
          f.platform_rules.split("\n")
            .map((line) => line.split(/:(.+)/).map((x) => x.trim()))
            .filter((p) => p[0] && p[1])
            .map(([k, v]) => [k, v])
        ),
      };
      await HTTP.patch(`/clients/${client.id}/brain`, payload);
      setEdit(false);
      setToast("Brand brain updated");
      await onSaved?.();
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); setTimeout(() => setToast(""), 3000); }
  };

  const addLearning = async () => {
    if (rule.trim().length < 3) return;
    setBusy(true);
    try {
      await HTTP.post(`/clients/${client.id}/learnings`, { rule: rule.trim() });
      setRule("");
      await onSaved?.();
      setToast("Learning added");
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Could not save learning");
    } finally { setBusy(false); setTimeout(() => setToast(""), 3000); }
  };

  if (!f) return null;

  return (
    <div className="brain-layout">
      <section className="panel" data-testid="brain-panel">
        <div className="panel-head">
          <div>
            <span className="section-kicker">Core Knowledge Store</span>
            <h2>{client.name} brain</h2>
          </div>
          {isEditor && !edit && (
            <button className="cta-outline" onClick={() => setEdit(true)} data-testid="edit-brain-button">
              <Edit3 size={14} /> Edit brain
            </button>
          )}
          {isEditor && edit && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="cta-outline" onClick={() => setEdit(false)} data-testid="cancel-brain-edit-button">Cancel</button>
              <button className="cta-primary" onClick={save} disabled={busy} data-testid="save-brain-button">
                {busy ? "Saving…" : "Save brain"}
              </button>
            </div>
          )}
        </div>

        {!edit ? (
          <>
            <div className="brain-block"><label>Positioning</label><p>{client.brain.positioning || <em style={{ color: "var(--muted-2)" }}>Not set</em>}</p></div>
            <div className="brain-block"><label>Who we speak to</label><p>{client.brain.audience || <em style={{ color: "var(--muted-2)" }}>Not set</em>}</p></div>
            <div className="brain-block">
              <label>Voice</label>
              <div className="voice-row">
                {client.brain.tone && <span>{client.brain.tone}</span>}
                {client.brain.no_go && <span className="no-go">NO-GO: {client.brain.no_go}</span>}
                {!client.brain.tone && !client.brain.no_go && <em style={{ color: "var(--muted-2)" }}>Not set</em>}
              </div>
            </div>
            <div className="brain-block">
              <label>Content pillars</label>
              <div className="pillars">
                {(client.brain.pillars || []).map((p) => <span key={p}>{p}</span>)}
                {(!client.brain.pillars || client.brain.pillars.length === 0) && <em style={{ color: "var(--muted-2)" }}>Not set</em>}
              </div>
            </div>
            <div className="brain-block">
              <label>Platform rules</label>
              <div className="rules-list">
                {Object.entries(client.brain.platform_rules || {}).map(([k, v]) => (
                  <div className="rule-row" key={k}><b>{k}</b><span>{v}</span></div>
                ))}
                {Object.keys(client.brain.platform_rules || {}).length === 0 && <em style={{ color: "var(--muted-2)" }}>Not set</em>}
              </div>
            </div>
          </>
        ) : (
          <>
            <label className="field"><label>Positioning</label>
              <textarea rows={2} value={f.positioning} onChange={(e) => setF({ ...f, positioning: e.target.value })} data-testid="brain-positioning-input" />
            </label>
            <label className="field"><label>Audience</label>
              <textarea rows={2} value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} data-testid="brain-audience-input" />
            </label>
            <label className="field"><label>Voice / Tone</label>
              <input value={f.tone} onChange={(e) => setF({ ...f, tone: e.target.value })} data-testid="brain-tone-input" />
            </label>
            <label className="field"><label>No-go</label>
              <input value={f.no_go} onChange={(e) => setF({ ...f, no_go: e.target.value })} data-testid="brain-nogo-input" />
            </label>
            <label className="field"><label>Content pillars (comma-separated)</label>
              <input value={f.pillars} onChange={(e) => setF({ ...f, pillars: e.target.value })} data-testid="brain-pillars-input" />
            </label>
            <label className="field"><label>Platform rules — one per line: <code>Platform: rule</code></label>
              <textarea rows={5} value={f.platform_rules} onChange={(e) => setF({ ...f, platform_rules: e.target.value })} data-testid="brain-platform-rules-input" />
            </label>
          </>
        )}
      </section>

      <section className="panel" data-testid="learnings-panel">
        <div className="panel-head">
          <div>
            <span className="section-kicker">Append-only</span>
            <h2>Latest learnings</h2>
          </div>
          <span className="count">{(client.brain.learnings || []).length} rules</span>
        </div>
        {(client.brain.learnings || []).slice().reverse().map((l, i) => (
          <div className="learning" key={`${l.date}-${i}`}>
            <span>{l.date}</span>
            <p>{l.rule}</p>
          </div>
        ))}
        {isEditor && (
          <div style={{ marginTop: 12 }}>
            <label className="field">
              <label>New learning rule</label>
              <input value={rule} onChange={(e) => setRule(e.target.value)}
                     placeholder="e.g. Always cite the source before making a claim"
                     data-testid="learning-input" />
            </label>
            <button className="learning-button" onClick={addLearning} disabled={busy} data-testid="add-learning-button">
              <Plus size={15} /> {busy ? "Saving…" : "Add a learning"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------- Pipeline run ----------
function PipelineRun({ client, run, onPicked, onClose }) {
  const [busy, setBusy] = useState(-1);
  const [msg, setMsg] = useState("");

  const pick = async (idx) => {
    setBusy(idx);
    try {
      const { data } = await HTTP.post(`/clients/${client.id}/runs/pick`, { run_id: run.id, topic_index: idx });
      setMsg(`Draft ${data.id} created · check the approval queue`);
      onPicked?.(data);
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Pick failed");
    } finally { setBusy(-1); }
  };

  return (
    <div>
      <div className="run-meta">
        <span className={`mode-chip ${run.signals_mode === "live" ? "live" : ""}`} data-testid="run-mode-chip">
          <span className="pulse" /> {run.signals_mode === "live" ? "LIVE SIGNALS" : "DEMO SIGNALS"}
        </span>
        <span className="run-id">{run.id}</span>
      </div>

      <span className="section-kicker" style={{ display: "block", marginTop: 16 }}>Sourced signals</span>
      <div className="run-signals">
        {(run.sourced_signals || []).map((s) => (
          <div className="run-signal" key={s.label}>
            <small>{s.label}</small>
            <strong>{s.value}</strong>
            <em>{s.delta}</em>
          </div>
        ))}
      </div>

      <span className="section-kicker" style={{ display: "block", marginTop: 20 }}>Topic candidates</span>
      {run.topics.map((t, i) => (
        <div className="run-topic" key={i} data-testid={`run-topic-${i}`}>
          <div>
            <span className="pillars"><span>{t.pillar}</span></span>
            {t.seeded_from_live_signals && <span className="demo-chip" style={{ marginLeft: 8, color: "var(--ok)", borderColor: "rgba(52,211,153,0.30)" }}>SIGNAL-DRIVEN</span>}
            <p style={{ marginTop: 8, color: "var(--text)", fontSize: 15, fontFamily: "var(--font-display)" }}>{t.topic}</p>
            <small style={{ color: "var(--muted)", fontSize: 12, display: "block", marginTop: 6 }}>{t.rationale}</small>
          </div>
          {t.materialized_draft_id ? (
            <span className="pending" style={{ color: "var(--ok)", borderColor: "rgba(52,211,153,0.30)" }}>✓ Drafted</span>
          ) : (
            <button className="cta-primary" onClick={() => pick(i)} disabled={busy !== -1} data-testid={`pick-topic-${i}-button`}>
              {busy === i ? "Drafting…" : "Pick & draft"}
            </button>
          )}
        </div>
      ))}
      <div className="mock-note" style={{ marginTop: 18 }}>
        <CircleDashed size={16} />
        <div>
          <b>NOTE</b>
          <p>{run.notes}</p>
        </div>
      </div>
      {msg && <div style={{ marginTop: 12, color: "var(--accent)", fontSize: 13 }} data-testid="run-message">{msg}</div>}
    </div>
  );
}

// ---------- Main App ----------
function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [signals, setSignals] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [run, setRun] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await HTTP.get("/auth/me");
        setUser(data);
      } catch { setUser(null); }
      setChecking(false);
    })();
  }, []);

  const loadWorkspace = useCallback(async (id) => {
    const [d, s] = await Promise.all([HTTP.get(`/drafts/${id}`), HTTP.get(`/signals/${id}`)]);
    setDrafts(d.data); setSignals(s.data);
  }, []);

  const refreshClients = useCallback(async (preferId) => {
    const { data } = await HTTP.get("/clients");
    setClients(data);
    const next = data.find((c) => c.id === (preferId || active?.id)) || data[0];
    setActive(next);
    if (next) await loadWorkspace(next.id);
    return next;
  }, [active?.id, loadWorkspace]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    HTTP.get("/clients").then(async (r) => {
      setClients(r.data);
      if (r.data[0]) { setActive(r.data[0]); await loadWorkspace(r.data[0].id); }
    }).finally(() => setLoading(false));
  }, [user, loadWorkspace]);

  const choose = async (c) => { setActive(c); setTab("overview"); await loadWorkspace(c.id); };

  const action = async (id, act) => {
    try {
      await HTTP.post(`/drafts/${id}/action`, { action: act });
      await loadWorkspace(active.id);
      setToast(act === "reject" ? "Draft rejected and added to the learning log" : "Approval recorded · scheduled safely");
      setTimeout(() => setToast(""), 3000);
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
      setTimeout(() => setToast(""), 3500);
    }
  };

  const logout = async () => { try { await HTTP.post("/auth/logout"); } catch {} setUser(null); };

  const createClient = async (payload) => {
    setBusy(true);
    try {
      const { data } = await HTTP.post("/clients", payload);
      setNewClientOpen(false);
      await refreshClients(data.id);
      setToast(`Workspace ${data.name} created`);
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); setTimeout(() => setToast(""), 3500); }
  };

  const updateClient = async (payload) => {
    setBusy(true);
    try {
      await HTTP.patch(`/clients/${active.id}`, payload);
      setEditClientOpen(false);
      await refreshClients(active.id);
      setToast("Workspace updated");
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); setTimeout(() => setToast(""), 3500); }
  };

  const deleteClient = async () => {
    if (!window.confirm(`Delete ${active.name} and all its drafts? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await HTTP.delete(`/clients/${active.id}`);
      setEditClientOpen(false);
      await refreshClients();
      setToast("Workspace deleted");
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); setTimeout(() => setToast(""), 3500); }
  };

  const startRun = async () => {
    setBusy(true);
    try {
      const { data } = await HTTP.post(`/clients/${active.id}/runs`);
      setRun(data);
      setRunOpen(true);
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Run failed");
      setTimeout(() => setToast(""), 3500);
    } finally { setBusy(false); }
  };

  if (checking) return <div className="loading" data-testid="app-loading">Loading…</div>;
  if (!user) return <Login onSuccess={setUser} />;
  if (loading || !active) return <div className="loading" data-testid="app-loading">Loading workspace…</div>;

  const initials = (user.name || user.email).slice(0, 2).toUpperCase();
  const isAdmin = user.role === "admin";
  const isEditor = user.role === "admin" || user.role === "approver";

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">B</div>
          <div>
            <b>BLOOPRINT</b>
            <span>Social · v0.2</span>
          </div>
        </div>
        <div className="workspace-label">
          <span>WORKSPACES</span>
          {isAdmin && (
            <button data-testid="add-client-button" onClick={() => setNewClientOpen(true)} title="New client"><Plus size={12} /></button>
          )}
        </div>
        <div className="client-list">
          {clients.map((c) => (
            <button key={c.id} className={`client-switch ${active.id === c.id ? "selected" : ""}`}
                    onClick={() => choose(c)} data-testid={`client-${c.id}-button`}>
              <span style={{ background: `linear-gradient(135deg, ${c.color}, ${c.accent})` }}>{c.initials}</span>
              <div>
                <strong>{c.name}</strong>
                <small>{c.descriptor}</small>
              </div>
              <i>{active.id === c.id ? "●" : ""}</i>
            </button>
          ))}
        </div>
        <div className="side-bottom">
          <div className="system-status" data-testid="system-status">
            <span className="dot"></span>
            <div>
              <b>System nominal</b>
              <small>Guardrails active</small>
            </div>
          </div>
          <button className="nav-button" onClick={() => setTab("mcp")} data-testid="open-mcp-button">
            <Terminal size={15} /> MCP surface
          </button>
          <div className="profile" data-testid="profile-badge">
            <span className="avatar">{initials}</span>
            <div>
              <b>{user.name || user.email.split("@")[0]}</b>
              <small>{user.role}</small>
            </div>
            <button className="logout" onClick={logout} data-testid="logout-button" title="Sign out"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div className="crumb">
            <span>Client Workspace</span>
            <ChevronDown size={14} />
            <strong>{active.name}</strong>
            {isAdmin && (
              <button className="icon-button" style={{ marginLeft: 8 }} onClick={() => setEditClientOpen(true)}
                      data-testid="edit-client-button" title="Edit workspace">
                <Edit3 size={14} />
              </button>
            )}
          </div>
          <div className="top-actions">
            <span className="mode-chip" data-testid="mode-chip">
              <span className="pulse"></span> Live Signals Ready
            </span>
            <button className="icon-button" data-testid="notifications-button"><Bell size={16} /></button>
            <div className="avatar-sm">{initials}</div>
          </div>
        </header>

        <section className="content">
          <div className="page-intro">
            <div>
              <div className="eyebrow">
                {active.descriptor} <span>·</span> {active.timezone}
              </div>
              <h1>{active.name}</h1>
              <p>One brand brain. Every decision traceable. Human approval always in the loop.</p>
            </div>
            {isEditor && (
              <button className="cta-primary" onClick={startRun} disabled={busy} data-testid="run-pipeline-button">
                <Sparkles size={16} /> {busy ? "Sourcing…" : "Run content pipeline"}
              </button>
            )}
          </div>

          <nav className="tabs" data-testid="workspace-navigation">
            <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")} data-testid="overview-tab"><Gauge size={14} /> Overview</button>
            <button className={tab === "brain" ? "active" : ""} onClick={() => setTab("brain")} data-testid="brain-tab"><Brain size={14} /> Brand brain</button>
            <button className={tab === "calendar" ? "active" : ""} onClick={() => setTab("calendar")} data-testid="calendar-tab"><CalendarDays size={14} /> Calendar</button>
            <button className={tab === "connectors" ? "active" : ""} onClick={() => setTab("connectors")} data-testid="connectors-tab"><ShieldCheck size={14} /> Connectors</button>
            <button className={tab === "mcp" ? "active" : ""} onClick={() => setTab("mcp")} data-testid="mcp-tab"><Terminal size={14} /> MCP</button>
          </nav>

          {tab === "overview" && (
            <>
              <div className="metrics">
                <Metric label="Drafts awaiting review" value={drafts.filter((d) => d.status === "review").length} detail="Human gate" tone="blue" icon={FileText} />
                <Metric label="Next scheduled" value={drafts.find((d) => d.status === "scheduled")?.schedule || "—"} detail={active.timezone} tone="cyan" icon={CalendarDays} />
                <Metric label="Signal mode" value={signals?.mocked ? "Demo" : "Live"} detail={signals?.mocked ? "Map real ids in Connectors" : "Meta + Google Ads"} tone={signals?.mocked ? "amber" : "mint"} icon={Radio} />
                <Metric label="Posting cadence" value={active.cadence.split(" ")[0]} detail="posts / week" tone="amber" icon={Zap} />
              </div>
              <div className="grid-main">
                <section className="panel" data-testid="drafts-panel">
                  <div className="panel-head">
                    <div>
                      <span className="section-kicker">Approval Queue</span>
                      <h2>Drafts that need your eye</h2>
                    </div>
                    <button className="text-button" data-testid="view-all-drafts-button">View all →</button>
                  </div>
                  {drafts.filter((d) => d.status === "review").length === 0 && (
                    <p style={{ color: "var(--muted)", marginTop: 12 }}>No drafts in the review queue right now.</p>
                  )}
                  {drafts.filter((d) => d.status === "review").map((d) => (
                    <Draft key={d.id} draft={d} onAction={action} canAct={isEditor} />
                  ))}
                </section>
                <section className="panel" data-testid="signals-panel">
                  <div className="panel-head">
                    <div>
                      <span className="section-kicker">Ad Signals {signals?.mocked ? <em>DEMO</em> : <em style={{ color: "var(--ok)", borderColor: "rgba(52,211,153,0.35)" }}>LIVE</em>}</span>
                      <h2>What the data is saying</h2>
                    </div>
                    <button className="icon-button" onClick={() => loadWorkspace(active.id)} data-testid="refresh-signals-button">↻</button>
                  </div>
                  {signals?.items.map((s, i) => (
                    <div className="signal" key={s.label}>
                      <div className={`signal-icon s${i}`}>
                        {i === 0 ? <Sparkles size={15} /> : i === 1 ? <Megaphone size={15} /> : <CircleDashed size={15} />}
                      </div>
                      <div>
                        <small>{s.label}</small>
                        <strong>{s.value}</strong>
                      </div>
                      <span>{s.delta}</span>
                    </div>
                  ))}
                  <div className="signal-foot">
                    <span>Last synced {signals?.updated}</span>
                    <span className="demo-chip">{signals?.mocked ? "MOCKED" : "LIVE"}</span>
                  </div>
                </section>
              </div>
            </>
          )}

          {tab === "brain" && <BrainView client={active} isEditor={isEditor} setToast={setToast} onSaved={() => refreshClients(active.id)} />}
          {tab === "calendar" && <Calendar drafts={drafts} />}
          {tab === "connectors" && <Connectors client={active} isAdmin={isAdmin} onSaved={() => refreshClients(active.id)} />}
          {tab === "mcp" && <McpPanel />}
        </section>
      </main>

      {toast && (
        <div className="toast" data-testid="toast-message">
          <Check size={16} /> {toast}
          <button data-testid="dismiss-toast-button" onClick={() => setToast("")}><X size={14} /></button>
        </div>
      )}

      <Modal open={newClientOpen} onClose={() => setNewClientOpen(false)}
             title="New client workspace" subtitle="Zero-code onboarding. Everything is editable later."
             wide testid="new-client-modal">
        <ClientForm isCreate onSave={createClient} busy={busy} />
      </Modal>

      <Modal open={editClientOpen} onClose={() => setEditClientOpen(false)}
             title={`Edit ${active.name}`} subtitle="Rename, restyle, adjust cadence or platforms." wide
             testid="edit-client-modal">
        <ClientForm initial={active} onSave={updateClient} onDelete={deleteClient} busy={busy} />
      </Modal>

      <Modal open={runOpen} onClose={() => setRunOpen(false)}
             title="Content pipeline · run results"
             subtitle="Topic candidates seeded from live ad signals. AI writing still MOCKED."
             wide testid="pipeline-run-modal">
        {run && <PipelineRun client={active} run={run} onPicked={async () => { await loadWorkspace(active.id); }} onClose={() => setRunOpen(false)} />}
      </Modal>
    </div>
  );
}

const Metric = ({ label, value, detail, tone, icon: Icon }) => (
  <div className="metric" data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}>
    <span className={`metric-icon ${tone}`}><Icon size={16} /></span>
    <small>{label}</small>
    <strong>{value}</strong>
    <em>{detail}</em>
  </div>
);

function Draft({ draft, onAction, canAct }) {
  return (
    <article className="draft" data-testid={`draft-${draft.id}`}>
      <div className="draft-top">
        <div>
          <span className="status-pill"><i /> Needs review</span>
          <span className="draft-topic">{draft.topic}</span>
        </div>
        <span className="draft-schedule"><CalendarDays size={13} /> {draft.schedule}</span>
      </div>
      <div className="draft-body">
        <div className="draft-copy">
          <p>{draft.content}</p>
          <div className="source"><ShieldCheck size={13} /> {draft.sources[0]}</div>
        </div>
        <div className="review">
          <span>Editorial second brain</span>
          <b className={draft.review.verdict === "APPROVE" ? "approved" : "revise"}>{draft.review.verdict}</b>
          <p>{draft.review.note}</p>
        </div>
      </div>
      <div className="draft-bottom">
        <div className="channels">
          {draft.platforms.map((p) => {
            const I = channelIcon[p] || Megaphone;
            return <span key={p}><I size={13} />{p}</span>;
          })}
        </div>
        {canAct && (
          <div className="draft-actions">
            <button className="reject" onClick={() => onAction(draft.id, "reject")} data-testid={`reject-${draft.id}-button`}>Reject</button>
            <button className="approve" onClick={() => onAction(draft.id, "approve")} data-testid={`approve-${draft.id}-button`}>
              <Check size={14} /> Approve & schedule
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

const Calendar = ({ drafts }) => {
  const rows = drafts.filter((d) => d.status === "scheduled");
  return (
    <section className="panel" data-testid="calendar-panel">
      <div className="panel-head">
        <div>
          <span className="section-kicker">Publish Queue</span>
          <h2>Upcoming schedule</h2>
        </div>
        <button className="cta-outline" data-testid="calendar-view-button">This week <ChevronDown size={14} /></button>
      </div>
      {rows.length === 0 && <p style={{ color: "var(--muted)" }}>Nothing scheduled yet.</p>}
      {rows.map((d, i) => (
        <div className="calendar-row" key={d.id} data-testid={`calendar-row-${d.id}`}>
          <div className="cal-date">
            <b>{i % 2 ? "SAT" : "FRI"}</b>
            <strong>{20 + i}</strong>
          </div>
          <div>
            <strong>{d.topic}</strong>
            <p>{d.pillar} · {d.platforms.join(" · ")}</p>
          </div>
          <span className="scheduled"><Check size={13} /> Scheduled</span>
          <span className="row-time">{d.schedule}</span>
        </div>
      ))}
    </section>
  );
};

const Connectors = ({ client, isAdmin, onSaved }) => {
  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState(null);
  const [meta, setMeta] = useState(client.ad_accounts.meta || "");
  const [google, setGoogle] = useState(client.ad_accounts.google || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [metaChoices, setMetaChoices] = useState(null);
  const [googleChoices, setGoogleChoices] = useState(null);
  const [discovering, setDiscovering] = useState(false);

  useEffect(() => {
    setMeta(client.ad_accounts.meta || "");
    setGoogle(client.ad_accounts.google || "");
    setMsg("");
    (async () => {
      try {
        const [s, h] = await Promise.all([
          HTTP.get(`/connectors/${client.id}`),
          HTTP.get(`/connectors/${client.id}/health`),
        ]);
        setStatus(s.data); setHealth(h.data);
      } catch { setStatus(null); setHealth(null); }
    })();
  }, [client.id]);

  const discover = async () => {
    if (!isAdmin) return;
    setDiscovering(true);
    try {
      const [m, g] = await Promise.all([
        HTTP.get("/discover/meta").catch(() => ({ data: { accounts: [] } })),
        HTTP.get("/discover/google-ads").catch(() => ({ data: { accounts: [] } })),
      ]);
      setMetaChoices(m.data.accounts || []);
      setGoogleChoices(g.data.accounts || []);
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Discovery failed");
    } finally { setDiscovering(false); }
  };

  const save = async () => {
    if (!isAdmin) return;
    setBusy(true); setMsg("");
    try {
      await HTTP.patch(`/clients/${client.id}/ad-accounts`, { meta, google });
      const [s, h] = await Promise.all([
        HTTP.get(`/connectors/${client.id}`),
        HTTP.get(`/connectors/${client.id}/health`),
      ]);
      setStatus(s.data); setHealth(h.data);
      setMsg("Saved · signals cache cleared");
      onSaved?.();
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); setTimeout(() => setMsg(""), 4000); }
  };

  const healthBadge = (h) => {
    if (!h) return <span className="pending">Loading…</span>;
    const s = h.status;
    if (s === "connected") return <span className="pending" style={{ color: "var(--ok)", borderColor: "rgba(52,211,153,0.30)" }}>✓ Connected</span>;
    if (s === "not_configured") return <span className="pending">Not configured</span>;
    if (s === "no_account") return <span className="pending">No account mapped</span>;
    return <span className="pending" style={{ color: "var(--danger)", borderColor: "rgba(248,113,113,0.35)" }}>{s}</span>;
  };

  const providerRows = [
    { key: "meta", n: "Meta Marketing API", a: client.ad_accounts.meta, icon: Megaphone, h: health?.meta },
    { key: "google_ads", n: "Google Ads API", a: client.ad_accounts.google, icon: Gauge, h: health?.google_ads },
    { key: "publish", n: "Publishing adapters", a: client.platforms.join(" · "), icon: Sparkles, h: null },
  ];

  return (
    <section className="panel connectors-panel" data-testid="connectors-panel">
      <div className="panel-head">
        <div>
          <span className="section-kicker">Integration Control</span>
          <h2>Connected platforms</h2>
        </div>
        <span className="safe-badge"><ShieldCheck size={13} /> Secrets stay server-side</span>
      </div>
      <p className="connector-intro">
        One app credential per platform (System User for Meta, MCC refresh token for Google Ads).
        Each client just needs its own <b>ad account id</b> mapped below.
      </p>

      {providerRows.map((x) => (
        <div className="connector-row" key={x.n}>
          <div className="connector-icon"><x.icon size={18} /></div>
          <div>
            <strong>{x.n}</strong>
            <small>
              {x.key === "publish"
                ? `Channels · ${x.a}`
                : `Account · ${x.a || "not set"}${x.h?.account?.name ? " · " + x.h.account.name : ""}`}
              {x.h?.detail && x.h.status !== "connected" ? ` — ${x.h.detail}` : ""}
            </small>
          </div>
          {x.key === "publish" ? <span className="pending">Credentials pending</span> : healthBadge(x.h)}
        </div>
      ))}

      {isAdmin && (
        <div style={{ marginTop: 22, padding: 16, borderRadius: 12, border: "1px solid var(--line)", background: "rgba(10,16,36,0.5)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span className="section-kicker">Ad account mapping (admin only)</span>
            <button className="cta-outline" onClick={discover} disabled={discovering} data-testid="discover-accounts-button">
              {discovering ? "Discovering…" : "Discover my accounts"}
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="field">
                <label>Meta ad account (act_XXX or digits)</label>
                <input value={meta} onChange={(e) => setMeta(e.target.value)} data-testid="meta-account-input" placeholder="act_1234567890" />
              </label>
              {metaChoices && (
                <div className="account-picker" data-testid="meta-account-picker">
                  {metaChoices.length === 0 && <p style={{ color: "var(--muted-2)", fontSize: 12 }}>No Meta accounts discovered.</p>}
                  {metaChoices.map((a) => (
                    <button key={a.id} type="button" className={`picker-item ${meta === a.id ? "selected" : ""}`}
                            onClick={() => setMeta(a.id)} data-testid={`meta-choice-${a.id}`}>
                      <strong>{a.name || "Unnamed"}</strong>
                      <small>{a.id} {a.currency ? `· ${a.currency}` : ""}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="field">
                <label>Google Ads customer id (dashes ok)</label>
                <input value={google} onChange={(e) => setGoogle(e.target.value)} data-testid="google-account-input" placeholder="123-456-7890" />
              </label>
              {googleChoices && (
                <div className="account-picker" data-testid="google-account-picker">
                  {googleChoices.length === 0 && <p style={{ color: "var(--muted-2)", fontSize: 12 }}>No Google client accounts discovered under MCC.</p>}
                  {googleChoices.map((a) => (
                    <button key={a.id_raw} type="button" className={`picker-item ${google === a.id ? "selected" : ""}`}
                            onClick={() => setGoogle(a.id)} data-testid={`google-choice-${a.id_raw}`}>
                      <strong>{a.name || "Unnamed"}</strong>
                      <small>{a.id} {a.currency ? `· ${a.currency}` : ""} {a.is_manager ? "· manager" : ""}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button className="cta-primary" style={{ marginTop: 12 }} onClick={save} disabled={busy} data-testid="save-accounts-button">
            {busy ? "Saving…" : "Save mapping"}
          </button>
          {msg && <span style={{ marginLeft: 12, color: "var(--muted)", fontSize: 12 }}>{msg}</span>}
        </div>
      )}

      <div className="mock-note" data-testid="connector-mode-notice" style={{ marginTop: 20 }}>
        <CircleDashed size={16} />
        <div>
          <b>{status?.mocked ? "DEMO MODE" : "LIVE PROVIDER MODE"}</b>
          <p>
            {status?.mocked
              ? "Provider app credentials missing — signals fall back to demo data."
              : "Meta & Google Ads credentials loaded from env. Live signals appear once a valid ad account is mapped for this client. Publishing adapters remain pending until OAuth wiring is added."}
          </p>
        </div>
      </div>
    </section>
  );
};

function McpPanel() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const r = await HTTP.post("/auth/refresh");
        const token = r.data.access_token;
        const info = await HTTP.get("/mcp", { headers: { Authorization: `Bearer ${token}` } });
        setInfo(info.data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  return (
    <section className="panel mcp-panel" data-testid="mcp-panel">
      <div className="panel-head">
        <div>
          <span className="section-kicker">Model Context Protocol</span>
          <h2>MCP surface</h2>
        </div>
        <span className="safe-badge"><KeyRound size={13} /> Bearer-authenticated</span>
      </div>
      <p style={{ color: "var(--muted)", marginBottom: 14 }}>
        A hosted MCP server (JSON-RPC 2.0 over Streamable HTTP) exposes read/write tools for the automation loop. Every call is client-scoped, role-guarded, and audited.
      </p>
      <div className="mcp-endpoint" data-testid="mcp-endpoint">
        <b>POST</b> {process.env.REACT_APP_BACKEND_URL}/api/mcp
      </div>
      {error && <div className="login-error" style={{ marginTop: 14 }}>{error}</div>}
      {info && (
        <div style={{ marginTop: 20 }}>
          <span className="section-kicker" style={{ marginBottom: 10, display: "block" }}>Tool catalog</span>
          {info.tools.map((t) => {
            const isWrite = ["append_learning","approve_draft","reject_draft","edit_draft","schedule_draft","publish_draft"].includes(t.name);
            return (
              <div className="mcp-tool" key={t.name} data-testid={`mcp-tool-${t.name}`}>
                <span className="mcp-tool-name">{t.name}</span>
                <span className="mcp-tool-desc">{t.description}</span>
                <span className={`mcp-tool-role ${isWrite ? "write" : ""}`}>{isWrite ? "write" : "read"}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default App;
